import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';

/**
 * Flat single-select dropdown for a product card's grievance level
 * (Figma node 2328:65422). Portal-rendered and positioned off the
 * trigger's bounding rect, same pattern as ItemPickerDropdown/
 * ComplaintTypeDropdown - the product card sits inside accordion/card
 * wrappers that clip overflow to keep their rounded corners, which would
 * otherwise cut an inline-rendered panel off.
 */
const GrievanceDropdown = ({
  options = [],
  value = null,
  onChange = () => {},
  placeholder = 'Select grievance level',
}) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  useLayoutEffect(() => {
    if (!open) return undefined;

    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const top = rect.bottom + 4;
      setPosition({
        top,
        left: rect.left,
        width: rect.width,
        maxHeight: Math.max(120, Math.min(280, window.innerHeight - top - 16)),
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (
        triggerRef.current?.contains(event.target) ||
        panelRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const selected = options.find((option) => option.value === value);

  const handlePick = (option) => {
    onChange(option.value);
    setOpen(false);
  };

  return (
    <div className='tw-w-full'>
      <button
        ref={triggerRef}
        type='button'
        onClick={() => setOpen((prev) => !prev)}
        className='tw-flex tw-items-start tw-justify-between tw-gap-2 tw-w-full tw-bg-white tw-border tw-border-solid tw-border-[#d2d6db] tw-rounded-[4px] tw-px-[10px] tw-py-[6px] tw-cursor-pointer'
      >
        <span
          className={`tw-min-w-0 tw-flex-1 tw-text-[12px] tw-leading-[16px] tw-text-left tw-whitespace-normal tw-break-words ${
            selected ? 'tw-text-[#111927]' : 'tw-text-[#6c737f]'
          }`}
        >
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown
          size={14}
          className={`tw-text-[#6c737f] tw-shrink-0 tw-mt-[1px] tw-transition-transform tw-duration-200 ${
            open ? 'tw-rotate-180' : ''
          }`}
        />
      </button>

      {open &&
        position &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              width: position.width,
              maxHeight: position.maxHeight,
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
              zIndex: 9999,
            }}
            className='tw-bg-white tw-border tw-border-solid tw-border-[#e5e7eb] tw-rounded-[8px] tw-shadow-[0_4px_16px_rgba(0,0,0,0.12)] tw-py-[4px]'
          >
            {options.map((option) => (
              <div
                key={option.value}
                role='button'
                tabIndex={0}
                onClick={() => handlePick(option)}
                className={`tw-flex tw-items-start tw-justify-between tw-gap-2 tw-px-[10px] tw-py-[6px] tw-cursor-pointer tw-text-[12px] tw-leading-[16px] tw-border-0 tw-border-b tw-border-solid tw-border-[#f3f4f6] last:tw-border-b-0 ${
                  option.value === value
                    ? 'tw-bg-[#ecfdf3] tw-text-base-green tw-font-medium'
                    : 'tw-text-[#111927] hover:tw-bg-[#f9fafb]'
                }`}
              >
                <span className='tw-min-w-0 tw-flex-1 tw-whitespace-normal tw-break-words'>
                  {option.label}
                </span>
                {option.value === value && (
                  <Check size={14} className='tw-shrink-0 tw-mt-[1px]' />
                )}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default GrievanceDropdown;
